<template>
  <div class="volunteer-time-container">
    <!-- Need Header Section -->
    <div class="need-header" v-if="need">
      <h1 class="need-title" @click="goToNeed">
        {{ need.title }}
        <v-icon small color="primary">mdi-open-in-new</v-icon>
      </h1>
    </div>

    <!-- Agency Card -->
    <v-card class="agency-card elevation-2" v-if="need">
      <v-card-title class="agency-card-header">
        <div class="agency-info" @click="goToProfile">
          <editable-avatar
           v-if="agency"
            :image="agency.midThumbnail ? agency.midThumbnail : agency.image"
            :editable="false"
            :size="$vuetify.breakpoint.xs ? 40 : 60"
          />
          <div class="agency-name" v-if="agency">
            {{ agency.name }}
            <v-chip small class="ml-2" color="primary" text-color="white" v-if="need.start_date">
              Started {{ formatDate(need.start_date) }}
            </v-chip>
          </div>
        </div>
        
        <v-btn
          color="primary"
          @click="startTimeTrack"
          class="track-time-btn"
          depressed
          dark
        >
          <v-icon left>mdi-clock-outline</v-icon>
          Record Work Time
        </v-btn>
      </v-card-title>

      <!-- Time Summary Cards -->
      <v-card-text class="time-summary-container">
        <v-row class="time-summary-row">
          <v-col v-for="(period, index) in timePeriods" :key="index" cols="6" sm="3">
            <div class="time-summary-card">
              <div class="time-period">{{ period.label }}</div>
              <div class="time-value">
                {{ summary[period.key] ? formatDifferent(summary[period.key]) : '0:00' }}
                <span class="time-unit">hrs</span>
              </div>
              <div class="time-trend" v-if="index > 0">
                <v-icon small :color="period.trend > 0 ? 'success' : 'error'">
                  mdi-arrow-{{ period.trend > 0 ? 'up' : 'down' }}-bold
                </v-icon>
                {{ Math.abs(period.trend) }}%
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Weekly Timesheet -->
    <v-card class="timesheet-card elevation-2 mt-4">
      <v-card-title class="timesheet-title">
        <v-icon left color="primary">mdi-calendar-week</v-icon>
        Timesheet This Week
      </v-card-title>
      
      <v-card-text>
        <div class="timesheet-grid">
          <div class="timesheet-header" v-for="(week, index) in weekNames" :key="index">
            <div class="weekday">{{ week }}</div>
            <div class="date" v-if="weekDates.length > index">{{ weekDates[index] }}</div>
          </div>
          <div class="timesheet-header">
            <div class="weekday">Total</div>
          </div>
          
          <template v-for="(weekhour, index) in weekHours">
            <div 
              class="timesheet-cell" 
              :key="'cell-'+index"
              :class="{ 'today-cell': todayWeekIndex === index }"
            >
              <div v-if="todayWeekIndex >= index">
                {{ weekhour ? formatDifferent(weekhour) : "-" }}
              </div>
            </div>
          </template>
          
          <div class="timesheet-cell total-cell">
            {{ formatDifferent(weekTotal) }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Work Diary -->
    <v-card class="diary-card elevation-2 mt-4 mb-6">
      <v-card-title class="diary-title">
        <v-icon left color="primary">mdi-notebook-edit-outline</v-icon>
        Work Diary
        
        <!-- <v-btn
          color="primary"
          @click="addManualTime"
          class="ml-auto"
          depressed
          dark
          small
        >
          <v-icon left small>mdi-plus</v-icon>
          Add Manual Time
        </v-btn> -->
      </v-card-title>
      
      <!-- Date Navigation -->
      <v-card-text class="date-navigation">
        <v-btn
          icon
          @click="selectPreviousDay"
          v-if="diaryDate"
          class="nav-btn"
        >
          <v-icon color="primary">mdi-chevron-left</v-icon>
        </v-btn>
        
        <v-menu
          v-model="date_picker"
          :close-on-content-click="false"
          :return-value.sync="diaryPickerDate"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="diaryDate"
              readonly
              solo
              flat
              outlined
              dense
              v-bind="attrs"
              v-on="on"
              autocomplete="off"
              append-icon="mdi-calendar-month-outline"
              hide-details
              class="date-picker-input"
            />
          </template>
          <v-date-picker
            v-model="diaryPickerDate"
            no-title
            scrollable
            show-adjacent-months
            :max="maxDate"
            color="primary"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="date_picker = false">
              Cancel
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="onSelectDiaryDate(diaryPickerDate)"
            >
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>
        
        <v-btn
          icon
          @click="selectNextDay"
          v-if="diaryDate && !isToday"
          class="nav-btn"
        >
          <v-icon color="primary">mdi-chevron-right</v-icon>
        </v-btn>
        
        <v-btn 
          text 
          @click="selectToday" 
          color="primary"
          class="today-btn"
        >
          Today
        </v-btn>
      </v-card-text>
      
      <!-- Daily Summary -->
      <v-card-text class="daily-summary">
        <div class="summary-row">
          <div class="summary-item">
            <span class="summary-label">Total:</span>
            <span class="summary-value">{{ formatDifferent(dayTotal) }} hrs</span>
          </div>
          <v-divider vertical class="mx-2"></v-divider>
          <div class="summary-item">
            <span class="summary-label">Tracked:</span>
            <span class="summary-value">{{ formatDifferent(dayTracked) }} hrs</span>
          </div>
          <v-divider vertical class="mx-2"></v-divider>
          <div class="summary-item">
            <span class="summary-label">Manual:</span>
            <span class="summary-value">{{ formatDifferent(dayManual) }} hrs</span>
          </div>
        </div>
      </v-card-text>
      
      <!-- Time Entries Table -->
      <v-card-text class="time-entries">
        <v-data-table
          :headers="headers"
          :items="diaryList"
          :items-per-page="10"
          :loading="loading"
          class="elevation-0 diary-table"
          :mobile-breakpoint="0"
        >
          <template v-slot:[`item.time`]="{ item }">
            <div class="time-entry" :class="{ 'rejected-entry': item.availability == 'Rejected' }">
              <div class="time-range">
                {{ formatTime(item.start) }} – 
                {{ item.status == "Tracking" ? "now" : formatTime(item.end) }}
              </div>
              <div class="time-duration">
                ({{ item.status == "Tracking" ? calculateDiff(item.start) : formatDifferent(item.minutes) }} hrs)
              </div>
            </div>
          </template>
          
          <template v-slot:[`item.type`]="{ item }">
            <v-chip
              small
              :color="getTypeColor(item)"
              text-color="white"
              class="type-chip"
            >
              {{ getTypeText(item) }}
            </v-chip>
          </template>
          
          <template v-slot:[`item.shift`]="{ item }">
            <div class="shift-name">
              {{ item.shift.title }}
            </div>
          </template>
          
          <template v-slot:[`item.action`]="{ item }">
            <div class="action-buttons">
              <!-- <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    @click="onEditDiaryItem(item)"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="item.type == 'Tracked'"
                    class="action-btn edit-btn"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip> -->
              
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    @click="onDeleteDiaryItem(item)"
                    v-bind="attrs"
                    v-on="on"
                    class="action-btn delete-btn"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </div>
          </template>
          
          <template v-slot:no-data>
            <div class="no-entries">
              <v-icon color="grey lighten-1" large>mdi-clock-time-eight-outline</v-icon>
              <div class="no-entries-text">No time entries for this day</div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialogs -->
    <volunteer-time-tracker
      :dialog="timeTrackDialog"
      :onCloseDialog="closeTimeTrackDialog"
      :need_id="need._id"
      v-if="profile && need"
    />
    
    <volunteer-add-manual-time
      :dialog="addManualDialog"
      :onAddedTime="onAddedTime"
      :onCloseDialog="onCloseTimeDialog"
      :responses="responses"
      :initDate="diaryDate"
      v-if="responses && responses.length && addManualDialog"
    />
    
    <volunteer-edit-manual-time
      :dialog="editManualDialog"
      :onEditedTime="onEditedTime"
      :onCloseDialog="onCloseTimeDialog"
      :responses="responses"
      :timeslot="selectedItem"
      v-if="editManualDialog && selectedItem"
    />
    
    <volunteer-delete-manual-time
      :dialog="deleteManualDialog"
      :onDeletedTime="onDeletedTime"
      :onCloseDialog="onCloseTimeDialog"
      :responses="responses"
      :timeslot="selectedItem"
      v-if="selectedItem && deleteManualDialog"
    />
    
    <volunteer-delete-auto-time
      :dialog="deleteAutoDialog"
      :onDeletedTime="onDeletedTime"
      :onCloseDialog="onCloseTimeDialog"
      :responses="responses"
      :timeslot="selectedItem"
      v-if="selectedItem && deleteAutoDialog"
    />
    
    <v-snackbar v-model="snackbar" :timeout="3000" color="primary">
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";
import dateFormat from "dateformat";
import VolunteerTimeTracker from "./VolunteerTimeTracker.vue";
import VolunteerAddManualTime from "./VolunteerAddManualTime.vue";
import VolunteerEditManualTime from "./VolunteerEditManualTime.vue";
import VolunteerDeleteManualTime from "./VolunteerDeleteManualTime.vue";
import VolunteerDeleteAutoTime from "./VolunteerDeleteAutoTime.vue";

export default {
  components: {
    EditableAvatar,
    VolunteerTimeTracker,
    VolunteerAddManualTime,
    VolunteerEditManualTime,
    VolunteerDeleteManualTime,
    VolunteerDeleteAutoTime
  },
  data() {
    return {
      loading: false,
      need: null,
      snackbar: false,
      snackMessage: null,
      agency: null,
      weekNames: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      weekDates: [],
      weekHours: [],
      weekTotal: 0,
      todayWeekIndex: 0,
      today: this.convertTZ("", this.timezone),
      headers: [
        { text: "Time", value: "time", width: "180px" },
        { text: "Type", value: "type", width: "120px" },
        { text: "Shift", value: "shift", width: "150px" },
        { text: "Memo", value: "memo" },
        { text: "Actions", value: "action", width: "120px", align: "right", sortable: false }
      ],
      diaryList: [],
      diaryDate: "",
      selectedDate: "",
      date_picker: false,
      diaryPickerDate: null,
      maxDate: null,
      addManualDialog: false,
      editManualDialog: false,
      deleteManualDialog: false,
      deleteAutoDialog: false,
      responses: [],
      dayTotal: 0,
      dayTracked: 0,
      dayManual: 0,
      summary: {},
      selectedItem: null,
      timeTrackDialog: false,
      timePeriods: [
        { label: "Last 24 hrs", key: "lastday", trend: 0 },
        { label: "This week", key: "week", trend: 5 },
        { label: "Last week", key: "lastWeek", trend: -3 },
        { label: "Since start", key: "total", trend: 15 }
      ]
    };
  },
  methods: {
    ...mapActions({
      getNeedDetails: "need/getNeedDetails",
      getAgency: "agency/getAgency",
      fetchUserResponses: "response/fetchUserResponses",
      fetchSummary: "timeslot/fetchSummary",
      fetchDayTimeSlots: "timeslot/fetchDayTimeSlots",
      fetchWeekTimeSheet: "timeslot/fetchWeekTimeSheet",
      fetchProfile: "auth/fetchProfile"
    }),
    getNeed() {
      this.loading = true;
      this.getNeedDetails({ _id: this.$route.query.need_id })
        .then(need => {
          this.loading = false;
          this.need = need;
          this.getAgencyDetails();
        })
        .catch(error => {
          this.loading = false;
          if (error.response != undefined && error.response.status === 401) {
            this.tokenDialog = true;
          } else {
            console.log(error);
          }
        });
    },
    getUserResponses() {
      if (!this.$route.query.need_id || !this.profile._id) return;
      this.loading = true;
      this.fetchUserResponses({
        need_id: this.$route.query.need_id,
        user_id: this.profile._id
      })
        .then(res => {
          this.loading = false;
          this.responses = res.map((res, index) => {
            if (!res.shift.title) {
              const shift = { ...res.shift, title: `Shift ${index + 1}` };
              return { ...res, shift };
            }
            return res;
          });
        })
        .catch(error => {
          this.loading = false;
          if (error.response != undefined && error.response.status === 401) {
            this.tokenDialog = true;
          } else {
            console.log(error.response.data.message);
          }
        });
    },
    getAgencyDetails() {
      this.getAgency({ _id: this.need.agency })
        .then(res => {
          this.loading = false;
          this.agency = res;
          this.getUserResponses();
        })
        .catch(error => {
          this.loading = false;
          this.$router.push({
            name: "404"
          });
          console.log(error);
        });
    },
    goToNeed() {
      this.$router.push({
        name: "agency-need-details",
        query: {
          _id: this.need._id
        }
      });
    },
    goToProfile() {
      this.$router.push({
        name: "agency-profile-details",
        params: { id: this.agency._id }
      });
    },
    formatTime(date) {
      return dateFormat(this.convertTZ(date, this.timezone), "h:MM TT");
    },
    formatDate(date) {
      return dateFormat(this.convertTZ(date, this.timezone), "mmm d, yyyy");
    },
    formatDifferent(diff) {
      const hrs = Math.floor(diff / 60);
      const mins = Math.floor(diff % 60);
      return `${hrs}:${mins < 10 ? '0' + mins : mins}`;
    },
    convertTZ(date, tzString) {
      if (!date) {
        return new Date(
          new Date().toLocaleString("en-US", {
            timeZone: tzString ? tzString : "America/New_York"
          })
        );
      }
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString ? tzString : "America/New_York"
          }
        )
      );
    },
    loadTimeSheet() {
      this.todayWeekIndex = (this.today.getDay() + 6) % 7;
      let sheetDay = this.convertTZ("", this.timezone);
      sheetDay.setDate(this.today.getDate() - this.todayWeekIndex);
      let endDay = this.convertTZ("", this.timezone);
      endDay.setDate(sheetDay.getDate() + 8);
      this.weekDates = [];
      while (sheetDay.getTime() < endDay.getTime()) {
        this.weekDates.push(`${sheetDay.getMonth() + 1}/${sheetDay.getDate()}`);
        sheetDay.setDate(sheetDay.getDate() + 1);
      }
      this.getWeekSheet();
    },
    onSelectDiaryDate(dateStr) {
      this.selectedDate = dateStr;
      this.diaryDate = dateFormat(dateStr + "T00:00:00", "dddd, mmmm d, yyyy");
      this.date_picker = false;
    },
    selectToday() {
      this.diaryDate = dateFormat(this.today, "dddd, mmmm d, yyyy");
      this.selectedDate = dateFormat(this.today, "yyyy-mm-dd");
    },
    selectPreviousDay() {
      let dd = new Date(this.selectedDate);
      dd.setDate(dd.getDate() - 1);
      this.diaryDate = dateFormat(dd, "dddd, mmmm d, yyyy");
      this.selectedDate = dateFormat(dd, "yyyy-mm-dd");
    },
    selectNextDay() {
      let dd = new Date(this.selectedDate);
      dd.setDate(dd.getDate() + 1);
      this.diaryDate = dateFormat(dd, "dddd, mmmm d, yyyy");
      this.selectedDate = dateFormat(dd, "yyyy-mm-dd");
    },
    addManualTime() {
      this.addManualDialog = true;
    },
    onCloseTimeDialog() {
      this.addManualDialog = false;
      this.editManualDialog = false;
      this.deleteManualDialog = false;
      this.deleteAutoDialog = false;
    },
    onAddedTime(timeslot) {
      this.addManualDialog = false;
      for (let i = 0; i < this.responses.length; i++) {
        if (this.responses[i].shift._id == timeslot.shift_id) {
          timeslot = { ...timeslot, shift: this.responses[i].shift };
        }
      }
      this.diaryList.push(timeslot);
      this.calculateDayTotal();
      this.getWeekSheet();
    },
    onEditedTime(timeslot) {
      this.editManualDialog = false;
      for (let i = 0; i < this.responses.length; i++) {
        if (this.responses[i].shift._id == timeslot.shift_id) {
          timeslot = { ...timeslot, shift: this.responses[i].shift };
        }
      }
      this.diaryList = this.diaryList.map(d =>
        d._id == timeslot._id ? timeslot : d
      );
      this.calculateDayTotal();
      this.getWeekSheet();
    },
    onDeletedTime(timeslot) {
      this.deleteManualDialog = false;
      this.diaryList = this.diaryList.filter(d => d._id !== timeslot._id);
      this.calculateDayTotal();
      this.getWeekSheet();
    },
    onDeleteDiaryItem(item) {
      this.selectedItem = item;
      if (item.type == "Manual") {
        this.deleteManualDialog = true;
      } else if (item.type == "Tracked") {
        this.deleteAutoDialog = true;
      }
    },
    onEditDiaryItem(item) {
      this.selectedItem = item;
      this.editManualDialog = true;
    },
    getWeekSheet() {
      if (this.profile._id) {
        this.fetchWeekTimeSheet({
          user_id: this.profile._id,
          need_id: this.$route.query.need_id
        })
          .then(res => {
            this.loading = false;
            this.weekHours = res;
            this.weekTotal = 0;
            for (let i = 0; i < this.weekHours.length; i++) {
              this.weekTotal += this.weekHours[i];
            }
            while (this.weekHours.length < 7) {
              this.weekHours.push(0);
            }
          })
          .catch(error => {
            this.loading = false;
            console.log(error.response.data.message);
          });
        this.getSummary();
      }
    },
    getSummary() {
      this.fetchSummary({
        user_id: this.profile._id,
        need_id: this.$route.query.need_id
      })
        .then(res => {
          this.summary = res;
        })
        .catch(error => {
          this.loading = false;
          console.log(error.response.data.message);
        });
    },
    getTimeSlots() {
      if (this.profile._id) {
        this.loading = true;
        this.fetchDayTimeSlots({
          user_id: this.profile._id,
          date: this.selectedDate,
          need_id: this.$route.query.need_id
        })
          .then(res => {
            this.loading = false;
            this.diaryList = res;
            this.calculateDayTotal();
          })
          .catch(error => {
            this.loading = false;
            console.log(error.response.data.message);
          });
      }
    },
    calculateDayTotal() {
      this.dayTotal = 0;
      this.dayTracked = 0;
      this.dayManual = 0;
      for (let i = 0; i < this.diaryList.length; i++) {
        this.dayTotal += this.diaryList[i].minutes;
        if (this.diaryList[i].type == "Tracked") {
          this.dayTracked += this.diaryList[i].minutes;
        } else if (this.diaryList[i].type == "Manual") {
          this.dayManual += this.diaryList[i].minutes;
        }
      }
      this.fetchProfile();
    },
    startTimeTrack() {
      this.timeTrackDialog = true;
    },
    closeTimeTrackDialog() {
      this.timeTrackDialog = false;
      this.getTimeSlots();
    },
    calculateDiff(start) {
      const startTime = this.convertTZ(start, this.timezone);
      const currentTime = this.convertTZ("", this.timezone);
      const diff = (currentTime - startTime) / 1000 / 60;
      return this.formatDifferent(diff);
    },
    getTypeColor(item) {
      if (item.availability == "Rejected") return "error";
      if (item.status == "Tracking") return "success";
      return item.type == "Tracked" ? "primary" : "secondary";
    },
    getTypeText(item) {
      if (item.availability == "Rejected") return "Rejected";
      if (item.status == "Tracking") return "Working now";
      return item.type;
    }
  },
  computed: {
    ...mapState("auth", {
      profile: "profile",
      profileType: "type"
    }),
    ...mapGetters({
      timezone: "usersetting/getTimeZone"
    }),
    isToday() {
      const date = new Date(this.diaryDate);
      return (
        this.today.getDate() == date.getDate() &&
        this.today.getMonth() == date.getMonth() &&
        this.today.getFullYear() == date.getFullYear()
      );
    }
  },
  watch: {
    diaryDate(newValue) {
      if (newValue && this.$route.query.need_id) {
        this.getTimeSlots();
      }
    },
    "profile._id": function() {
      this.getWeekSheet();
      this.getUserResponses();
      if (this.diaryDate && this.$route.query.need_id) {
        this.getTimeSlots();
      }
    }
  },
  mounted() {
    if (this.profileType != "Volunteer") {
      this.$router.push({
        name: "404"
      });
      return;
    }
    this.getNeed();
    this.today = this.convertTZ("", this.timezone);
    this.maxDate = dateFormat(this.today, "yyyy-mm-dd");
    this.diaryDate = dateFormat(this.today, "dddd, mmmm d, yyyy");
    this.selectedDate = dateFormat(this.today, "yyyy-mm-dd");
    this.loadTimeSheet();
  }
};
</script>

<style scoped>
.volunteer-time-container {
      min-width: 70%;
  margin: 0 auto;
  padding: 20px;
}

/* Need Header */
.need-header {
  margin-bottom: 24px;
}

.need-title {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
}

.need-title:hover {
  color: #0a5b8a;
}

/* Agency Card */
.agency-card {
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.agency-card-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.agency-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-grow: 1;
}

.agency-name {
  font-size: 18px;
  font-weight: 600;
  margin-left: 16px;
  display: flex;
  align-items: center;
}

.track-time-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Time Summary */
.time-summary-container {
  padding: 16px 24px;
}

.time-summary-row {
  margin: 0 -8px;
}

.time-summary-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;
}

.time-summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.time-period {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 8px;
}

.time-value {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
}

.time-unit {
  font-size: 16px;
  font-weight: normal;
  color: #64748b;
}

.time-trend {
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

/* Timesheet Card */
.timesheet-card {
  border-radius: 12px;
  overflow: hidden;
}

.timesheet-title {
  font-size: 18px;
  font-weight: 600;
  padding: 16px 24px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
}

.timesheet-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
  padding: 8px 0;
}

.timesheet-header {
  text-align: center;
  padding: 8px;
}

.weekday {
  font-weight: 600;
  font-size: 14px;
  color: #334155;
}

.date {
  font-size: 12px;
  color: #64748b;
}

.timesheet-cell {
  text-align: center;
  padding: 12px 8px;
  border-radius: 6px;
  background-color: #f8fafc;
  font-size: 14px;
}

.today-cell {
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  font-weight: 500;
}

.total-cell {
  background-color: #eff6ff;
  font-weight: 600;
  color: #1d4ed8;
}

/* Diary Card */
.diary-card {
  border-radius: 12px;
  overflow: hidden;
}

.diary-title {
  font-size: 18px;
  font-weight: 600;
  padding: 16px 24px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
}

.date-navigation {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background-color: #f8fafc;
}

.date-picker-input {
  max-width: 280px;
}

.nav-btn {
  margin: 0 4px;
}

.today-btn {
  margin-left: 12px;
}

.daily-summary {
  padding: 12px 24px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.summary-row {
  display: flex;
  align-items: center;
}

.summary-item {
  display: flex;
  align-items: center;
}

.summary-label {
  font-size: 14px;
  color: #64748b;
  margin-right: 4px;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.time-entries {
  padding: 0;
}

.diary-table {
  border-radius: 0 0 12px 12px;
}

.time-entry {
  display: flex;
  flex-direction: column;
}

.time-range {
  font-weight: 500;
}

.time-duration {
  font-size: 12px;
  color: #64748b;
}

.rejected-entry {
  opacity: 0.6;
  text-decoration: line-through;
}

.type-chip {
  font-size: 12px;
  height: 24px;
}

.shift-name {
  font-weight: 500;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
}

.action-btn {
  margin: 0 2px;
  width: 28px;
  height: 28px;
}

.edit-btn {
  color: #0a5b8a;
}

.delete-btn {
  color: #dc2626;
}

.no-entries {
  padding: 40px 0;
  text-align: center;
  color: #94a3b8;
}

.no-entries-text {
  margin-top: 8px;
  font-size: 14px;
}

/* Responsive Styles */
@media (max-width: 960px) {
  .volunteer-time-container {
    padding: 16px;
  }
  
  .time-summary-card {
    padding: 12px;
  }
  
  .time-value {
    font-size: 20px;
  }
  
  .timesheet-grid {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .timesheet-header:nth-child(n+5),
  .timesheet-cell:nth-child(n+5) {
    display: none;
  }
}

@media (max-width: 600px) {
  .agency-card-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 16px;
  }
  
  .track-time-btn {
    margin-top: 12px;
    width: 100%;
  }
  
  .time-summary-row {
    margin: 0 -4px;
  }
  
  .time-summary-card {
    padding: 8px;
  }
  
  .time-period {
    font-size: 12px;
  }
  
  .time-value {
    font-size: 18px;
  }
  
  .summary-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .summary-item {
    margin: 4px 0;
  }
  
  .v-divider {
    display: none;
  }
  
  .date-picker-input {
    max-width: 180px;
  }
}
</style>